import { type DynamicFilterValue, parseDynamicFilters } from '../../filters';

/**
 * Kit list / search status values accepted by {@link buildKitSearchFilters}.
 */
export enum KitStatusFilter {
	AVAILABLE = 'available',
	CHECKEDOUT = 'checkedout',
	AWAIT_CHECKOUT = 'await_checkout',
	IN_CUSTODY = 'in_custody',
	RETIRED = 'expired',
	EMPTY = 'empty',
	INCOMPLETE = 'incomplete',
}

const statusToListNameMap: Record<string, string> = {
	[KitStatusFilter.AVAILABLE]: 'available',
	[KitStatusFilter.CHECKEDOUT]: 'checkedout',
	[KitStatusFilter.AWAIT_CHECKOUT]: 'checkingout',
	[KitStatusFilter.IN_CUSTODY]: 'in_custody',
	[KitStatusFilter.RETIRED]: 'expired',
	[KitStatusFilter.EMPTY]: 'empty',
	[KitStatusFilter.INCOMPLETE]: 'incomplete',
};

export type KitListName = [string, 'or' | 'and'];

export type KitSearchFiltersQuery = {
	listName?: string;
	listNames?: KitListName[];
	locked?: boolean;
	[key: string]: unknown;
};

export type BuildKitSearchFiltersInput = {
	statuses?: string[];
	locked?: 'any' | 'locked' | 'unlocked';
	/** Extra field filters keyed by field id. */
	dynamicFilters?: Record<string, DynamicFilterValue | undefined>;
};

/**
 * Builds Python kit search / `customFieldFilters` params from neutral filter ids.
 * UI adapters should unwrap chip shapes before calling this.
 */
export const buildKitSearchFilters = (input: BuildKitSearchFiltersInput = {}): KitSearchFiltersQuery => {
	const { statuses, locked, dynamicFilters = {} } = input;

	const query: KitSearchFiltersQuery = {};

	const hasStatuses = Boolean(statuses && statuses.length > 0);

	if (hasStatuses && statuses) {
		const listNames: KitListName[] = [];

		for (const status of statuses) {
			const listName = statusToListNameMap[status];
			if (listName) {
				listNames.push([listName, 'or']);
			}
		}

		if (listNames.length > 0) {
			query.listNames = listNames;
		}
	} else {
		query.listName = 'active';
	}

	if (locked && locked !== 'any') {
		query.locked = locked === 'locked';
	}

	return {
		...query,
		...parseDynamicFilters(dynamicFilters),
	};
};
